import { Check, EyeOff, Search, ShieldX, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ClientVaultEventSelector } from "../../components/ClientVaultEventSelector";
import { PageHeader } from "../../components/PageHeader";
import { MediaCard } from "../../components/media/MediaCard";
import { getMedia, updateMediaAction } from "../../lib/api";
import { useClientVaultContext, vaultQueryParams } from "../../lib/useClientVaultContext";
import type { MediaItem, MediaKind, MediaStatus } from "../../lib/types";

type Action = "approve" | "not-approve" | "hide" | "feature" | "unfeature" | "delete";

const pageMeta: Record<
  string,
  {
    title: string;
    subtitle: string;
    status?: MediaStatus;
    featured?: boolean;
  }
> = {
  pending: {
    title: "Pending Review",
    subtitle: "These uploads are waiting for your approval before they appear in your Memory Media Vault or approved gallery.",
    status: "pending",
  },
  approved: {
    title: "Approved Gallery",
    subtitle: "Approved photos and videos can appear in the full approved gallery.",
    status: "approved",
  },
  featured: {
    title: "Featured in Vault",
    subtitle: "Featured media appears in the curated Memory Media Vault section.",
    featured: true,
  },
  hidden: {
    title: "Hidden / Private",
    subtitle: "Hidden media stays stored privately and does not appear publicly.",
    status: "hidden",
  },
  "not-approved": {
    title: "Not Approved",
    subtitle: "These uploads are rejected and not public.",
    status: "not_approved",
  },
};

export function MediaManagementPage({ view }: { view: keyof typeof pageMeta }) {
  const meta = pageMeta[view];
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState<MediaKind | "all">("all");
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const vaultContext = useClientVaultContext();

  const load = async () => {
    if (vaultContext.loading) return;
    if (!vaultContext.context || !vaultContext.selectedVault) {
      setMedia([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const items = await getMedia({
      ...vaultQueryParams(vaultContext.context, vaultContext.selectedVault),
      status: meta.status,
      featured: meta.featured,
      type,
      search,
    });
    setMedia(items.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)));
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, [view, type, vaultContext.loading, vaultContext.selectedVaultId]);

  const searchedMedia = useMemo(() => {
    if (!search) return media;
    const value = search.toLowerCase();
    return media.filter((item) => {
      return (
        item.uploaderName.toLowerCase().includes(value) ||
        item.fileName.toLowerCase().includes(value) ||
        item.caption?.toLowerCase().includes(value)
      );
    });
  }, [media, search]);

  const runAction = async (id: string, action: Action) => {
    try {
      const result = await updateMediaAction(id, action);
      const updated = result.media;
      const stillInView =
        (!meta.status || updated.status === meta.status) &&
        (meta.featured === undefined || updated.featured === meta.featured) &&
        (type === "all" || updated.fileType === type);
      setMedia((current) =>
        stillInView
          ? current.map((item) => (item.id === id ? updated : item))
          : current.filter((item) => item.id !== id),
      );
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Unable to update media.");
    }
  };

  const runBulk = async (action: Action) => {
    if (!selected.length) return;
    await Promise.all(selected.map((id) => runAction(id, action)));
    setSelected([]);
  };

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Memory Vault Review" title={meta.title} subtitle={meta.subtitle}>
        <div className="vault-rail rounded-lg border border-ktd-gold/25 bg-white/[0.06] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-ktd-softGold">Current View</p>
          <p className="mt-2 text-3xl font-black text-white">{searchedMedia.length}</p>
          <p className="mt-1 text-sm text-white/62">Newest to oldest</p>
        </div>
      </PageHeader>

      <ClientVaultEventSelector
        context={vaultContext.context}
        selectedVault={vaultContext.selectedVault}
        selectedVaultId={vaultContext.selectedVaultId}
        loading={vaultContext.loading}
        error={vaultContext.error}
        onChange={vaultContext.selectVault}
      />

      <section className="review-toolbar ivory-card rounded-lg p-4 sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_auto] lg:items-center">
          <label className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by uploader name, file name, or caption"
              className="form-field focus-ring w-full rounded-lg py-3 pl-11 pr-4 text-sm font-semibold"
            />
          </label>
          <select
            value={type}
            onChange={(event) => setType(event.target.value as MediaKind | "all")}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            <option value="all">All Media</option>
            <option value="photo">Photos Only</option>
            <option value="video">Videos Only</option>
          </select>
          <div className="flex flex-wrap gap-2">
            <BulkButton label="Bulk approve selected" icon={Check} onClick={() => void runBulk("approve")} />
            <BulkButton label="Bulk hide selected" icon={EyeOff} onClick={() => void runBulk("hide")} />
            <BulkButton label="Bulk feature selected" icon={Sparkles} onClick={() => void runBulk("feature")} />
            <BulkButton label="Bulk not approve selected" icon={ShieldX} onClick={() => void runBulk("not-approve")} />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="ivory-card rounded-lg p-10 text-center font-bold text-ktd-muted">Loading media...</div>
      ) : searchedMedia.length ? (
        <section className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
          {searchedMedia.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              selectable
              selected={selected.includes(item.id)}
              onSelect={(checked) =>
                setSelected((current) => (checked ? [...current, item.id] : current.filter((id) => id !== item.id)))
              }
              onAction={runAction}
            />
          ))}
        </section>
      ) : (
        <section className="ivory-card rounded-lg p-10 text-center">
          <Sparkles className="mx-auto size-12 text-[#8a6c14]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Nothing needs attention here.</h2>
          <p className="mt-3 text-ktd-muted">When media matches this view, it will appear in this elegant review space.</p>
        </section>
      )}
    </div>
  );
}

function BulkButton({
  label,
  icon: Icon,
  onClick,
}: {
  label: string;
  icon: typeof Check;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="focus-ring inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-ktd-gold/25 bg-white px-3 py-2 text-xs font-extrabold text-ktd-rich transition hover:bg-ktd-black hover:text-ktd-softGold"
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}
